// Server-side transcription fallback for voice input, imageProcessor-style: a pure helper that
// logs and returns null on any failure — never throws. The caller (useVoiceInput) decides what
// degradation means. Deliberately SDK-free (first-party WAV encoding) so the fallback still works
// when the @missionsquad/browserai chunk itself failed to load.

import { transcribeSpeech } from '@/utils/api'
import { isApiError } from '@/types/api'
import { isSpeechTranscriptionResponse } from '@/types/speechProcessing'
import { AUDIO_SR, encodeWavBlob } from '@/utils/micRecorder'

/**
 * Encode a recorded take as WAV and transcribe it through the backend speech-processing relay.
 * Applies the same post-processing as the browser-local path (trim, discard "[BLANK_AUDIO]"-style
 * bracket markers).
 *
 * @returns the transcript; `''` when the relay succeeded but the take contained no speech;
 *   `null` when the request failed (already logged) — never throws.
 */
export async function transcribeTakeServerSide(pcm: Float32Array): Promise<string | null> {
  try {
    const blob = encodeWavBlob(pcm, AUDIO_SR)
    const response = await transcribeSpeech(blob, 'take.wav')
    if (isApiError(response)) {
      console.error('[speechProcessing] server-side transcription failed:', response.message)
      return null
    }
    if (!isSpeechTranscriptionResponse(response)) {
      console.error('[speechProcessing] server-side transcription returned an unexpected payload:', response)
      return null
    }
    const trimmed = response.text.trim()
    if (!trimmed || /^\[.*\]$/.test(trimmed)) return ''
    return trimmed
  } catch (error) {
    console.error('[speechProcessing] server-side transcription failed:', error)
    return null
  }
}
